import { useEffect } from "react";
import { motion } from "framer-motion";
import { FandomQuiz } from "@/components/FandomQuiz";
import { CharacterGallery } from "@/components/CharacterGallery";
import { PairSets } from "@/components/PairSets";
import { RevealLines } from "@/components/Reveal";
import { WH, WH_PAIRS } from "@/components/witchHatData";
import { WH_CHARM } from "@/components/charms";

const ACCENT = {
  text: "text-sky-300", soft: "text-sky-200", border: "border-sky-200/25", bar: "bg-sky-400",
  hover: "hover:border-sky-300 hover:bg-sky-900/30", glow: "rgba(56,189,248,0.18)",
  panel: "bg-[#0a1430]/85", answer: "border-sky-200/25 bg-[#050a1c]/60",
};

const Cover = () => (
  <motion.div initial={{ opacity: 0, y: 40, rotate: 0 }} animate={{ opacity: 1, y: 0, rotate: -3 }}
    transition={{ duration: 1.1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
    className="relative mx-auto w-full max-w-[340px] lg:max-w-[400px]">
    <div className="absolute -inset-6 rounded-[32px] bg-sky-500/20 blur-3xl" aria-hidden="true" />
    <img src="/img/witchhat-cover.jpg" alt="Witch Hat Atelier poster with Coco, Qifrey, Olruggio, Agott, Richeh and Tetia"
      data-testid="witchhat-cover"
      className="relative w-full h-auto rounded-[20px] border border-sky-200/30 shadow-[0_30px_80px_-20px_rgba(56,189,248,0.5)]" />
  </motion.div>
);

export default function WitchHatPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <main className="relative pt-28" data-testid="witchhat-page">
      <div className="fixed inset-0 -z-10 bg-[#050a1c]" data-testid="witchhat-bg" aria-hidden="true"
        style={{ backgroundImage: "radial-gradient(60% 50% at 85% 10%, rgba(56,189,248,0.22), transparent 70%), radial-gradient(45% 40% at 10% 85%, rgba(233,185,73,0.16), transparent 70%), radial-gradient(40% 35% at 50% 50%, rgba(43,45,110,0.35), transparent 70%)" }} />
      <section className="relative max-w-[1400px] mx-auto px-5 md:px-10 pt-10 pb-6 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-sky-200">Witch Hat Atelier · fan corner</p>
          <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.92] tracking-tight" data-testid="witchhat-title">
            <RevealLines lines={[<>Ink, sigils</>, <em className="text-sky-300">and beads.</em>]} />
          </h1>
          <p className="mt-6 max-w-lg text-purple-50/90 leading-relaxed">
            Draw your way into the atelier, find your match, then wear their colors. Every character's bracelet can be made by hand just for you.
          </p>
        </div>
        <div className="lg:col-span-5"><Cover /></div>
      </section>
      <FandomQuiz id="wh-quiz" prefix="wh" label="Atelier · personality" lines={["Who are you in", "the atelier?"]}
        intro="Answer seven questions to find your Witch Hat Atelier match." quiz={WH} accent={ACCENT} charm={WH_CHARM}>
        <CharacterGallery characters={WH.CHARACTERS} prefix="wh-gallery" accentText="text-sky-300" accentSoft="text-sky-200/90"
          blurb="Every character from the quiz, in their own colors. Pick one and make it yours for $10."
          cardClass="border-sky-200/20 bg-[#0a1430]/85 hover:border-sky-200/50" charm={WH_CHARM} />
        <PairSets pairs={WH_PAIRS} characters={WH.CHARACTERS} fandom="Witch Hat Atelier" prefix="wh-pairs" accent={ACCENT} charm={WH_CHARM} />
      </FandomQuiz>
    </main>
  );
}
